import { Eyebrow, displayFont } from "@/components/content-blocks/band-helpers";
import { VideoPlayer } from "@/components/video-player";
import { BlockCtaButtons } from "@/components/block-cta-buttons";
import type { ContentBlock } from "@/lib/storefront-types";

type VideoTextSectionProps = {
  block: Extract<ContentBlock, { kind: "videoText" }>;
  stackPosition?: "single" | "start" | "middle" | "end";
};

type VideoTextTheme = {
  sectionClassName: string;
  style?: React.CSSProperties;
  isDark: boolean;
  eyebrowClassName?: string;
  headlineClassName: string;
  bodyClassName: string;
  normalizeRichText: boolean;
};

const DEFAULT_THEME: VideoTextTheme = {
  sectionClassName: "bg-[#f1f7ee]",
  isDark: false,
  headlineClassName:
    "mt-4 max-w-xl text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight storefront-text-heading",
  bodyClassName: "mt-5 max-w-xl text-base leading-7 storefront-text-body sm:text-lg",
  normalizeRichText: false,
};

const VIDEO_TEXT_THEMES: Record<string, VideoTextTheme> = {
  bgLight: {
    ...DEFAULT_THEME,
    sectionClassName: "bg-[#f4f8f1]",
  },
  bgLightAlt: {
    ...DEFAULT_THEME,
  },
  bgWhite: {
    ...DEFAULT_THEME,
    sectionClassName: "bg-white",
  },
  bgDark: {
    sectionClassName: "storefront-surface-dark",
    isDark: true,
    headlineClassName:
      "mt-4 max-w-xl text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight text-white",
    bodyClassName: "mt-5 max-w-xl text-base leading-7 text-white/70 sm:text-lg",
    normalizeRichText: false,
  },
  bgDarkAlt: {
    sectionClassName: "storefront-surface-dark-alt border-y border-white",
    isDark: true,
    headlineClassName:
      "mt-4 max-w-[17ch] text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight text-white",
    bodyClassName:
      "mt-6 max-w-[49rem] text-[1.03rem] leading-8 text-white sm:text-[1.14rem] [&_*]:bg-transparent [&_*]:font-inherit [&_*]:text-inherit",
    normalizeRichText: true,
  },
  bgFrozen: {
    sectionClassName:
      "storefront-video-text-frozen-light border-y storefront-border-primary-soft",
    isDark: false,
    eyebrowClassName: "text-[rgba(var(--storefront-primary-dark-rgb),0.62)]",
    headlineClassName:
      "mt-4 max-w-[18ch] text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight text-[var(--storefront-primary-dark)]",
    bodyClassName:
      "mt-5 max-w-[46rem] text-[1.02rem] leading-8 text-[rgba(var(--storefront-primary-dark-rgb),0.76)] sm:text-[1.08rem] [&_*]:bg-transparent [&_*]:font-inherit [&_*]:text-inherit",
    normalizeRichText: true,
  },
  bgFrozenAlt: {
    sectionClassName:
      "storefront-video-text-frozen-dark border-y storefront-border-primary-soft",
    isDark: true,
    headlineClassName:
      "mt-4 max-w-[17ch] text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight text-white",
    bodyClassName:
      "mt-6 max-w-[49rem] text-[1.03rem] leading-8 text-white/82 sm:text-[1.14rem] [&_*]:bg-transparent [&_*]:font-inherit [&_*]:text-inherit",
    normalizeRichText: true,
  },
};

function normalizeThemeRichText(html: string) {
  return html.replace(/\sstyle=(["'])(?:(?!\1).)*\1/gi, "");
}

function resolveTheme(value: string | null | undefined): VideoTextTheme {
  if (!value) {
    return DEFAULT_THEME;
  }

  const theme = VIDEO_TEXT_THEMES[value];
  if (theme) {
    return theme;
  }

  return {
    ...DEFAULT_THEME,
    sectionClassName: "",
    style: { backgroundColor: value },
  };
}

export function VideoTextSection({
  block,
}: VideoTextSectionProps) {
  const theme = resolveTheme(block.backgroundColor);
  const isReversed = block.layoutDirection === "videoRight";
  const textShellClass = isReversed
    ? "w-full px-5 sm:px-8 lg:max-w-[50rem] xl:max-w-[56rem] 2xl:max-w-[60rem] lg:pl-[max(3rem,calc((100vw-96rem)/2+3rem))] lg:pr-10"
    : "w-full px-5 sm:px-8 lg:max-w-[50rem] xl:max-w-[56rem] 2xl:max-w-[60rem] lg:pl-10 lg:pr-[max(3rem,calc((100vw-96rem)/2+3rem))]";
  const bodyHtml = block.bodyHtml
    ? theme.normalizeRichText
      ? normalizeThemeRichText(block.bodyHtml)
      : block.bodyHtml
    : null;

  return (
    <section
      className={`w-full ${theme.sectionClassName}`}
      style={theme.style}
    >
      <div className="grid min-h-[28rem] gap-0 lg:grid-cols-[0.98fr_1.02fr]">
        <div
          className={`relative min-h-[20rem] overflow-hidden lg:min-h-[32rem] ${
            isReversed ? "order-2" : ""
          }`}
        >
          <VideoPlayer
            url={block.video.url}
            title={block.video.title}
            className="absolute inset-0 h-full w-full"
            immersive
          />
        </div>

        <div
          className={`flex flex-col justify-center py-10 lg:py-16 ${
            isReversed ? "order-1" : ""
          }`}
        >
          <div className={textShellClass}>
            {block.eyebrow ? (
              <Eyebrow
                inverse={theme.isDark}
                className={theme.eyebrowClassName ?? ""}
              >
                {block.eyebrow}
              </Eyebrow>
            ) : null}

            <h2
              className={`${displayFont.className} ${theme.headlineClassName}`}
            >
              {block.headline}
            </h2>

            {bodyHtml ? (
              <div
                className={theme.bodyClassName}
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            ) : null}

            {block.buttons.length > 0 ? (
              <BlockCtaButtons buttons={block.buttons} className="mt-8" />
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
